<template lang="pug">
  div
    v-navigation-drawer(
      v-model="drawer"
      :color="$vuetify.theme.currentTheme.drawer"
      class="drawer"
      app
      id="app-sidebar"
      permanent
      clipped
      width="200"
      fixed
      floating
      ref="drawer"
    )
      template(v-slot:prepend)
        div(class="d-flex align-center justify-center mb-10")
          nuxt-link(
            to="/documents"
            exact
          )
            v-img(
              :src="require('~/assets/images/logo/logo-black.svg')"
              max-width="152"
            )
      div(class="mb-6")
        e-button-template-upload(
          v-if="$route.name === 'templates'"
          ref="createButton"
          class="w-100"
        )
        e-button-contact-create(
          v-else-if="$route.name === 'contacts'"
          ref="createButton"
          class="w-100"
        )
        e-button-document-upload(
          v-else
          ref="createButton"
          :full-width="true"
          class="w-100"
        )
      v-list(ref="navigation")
        v-list-item-group(v-model="activeLink")
          template(v-for="(link, index) in items")
            nuxt-link(
              v-if="isVisible(link, $Organization)"
              :to="link.href"
              class="d-flex align-center link"
              exact
              active-class="link--active"
              :class="{ 'link--spacer': link.spacer }"
            )
              v-list-item(
                :key="index"
                class="list__item"
              )
                v-list-item-icon(class="mr-0")
                  e-svg-icon(:name="isRouteActive(link.href) ? link.activeIcon : link.icon")
                v-list-item-title(class="link__text pl-3") {{ $t(link.text) }}
                span(
                  v-if="link.isNew"
                  class="link__new"
                ) New
                v-img(
                  v-if="link.premiumRequired && !$hasProVersion"
                  :src="require('~/assets/images/lock.png')"
                  :max-width="20"
                )
          nuxt-link(
            v-if="$isSuperAdmin"
            to="/admin"
            class="d-flex align-center link"
            exact
            active-class="link--active"
          )
            v-list-item(class="list__item")
              v-list-item-title(class="link__text") {{ $t('Go to admin') }}
      billing-usage-progress(
        v-if="$Organization"
        ref="billingUsage"
      )
      m-block-activate-premium(v-else-if="!$Organization && !organizations.length")
      v-btn(
        class="tags-btn"
        ref="createTagBtn"
        @click="createTag"
      )
        v-list-item-title(class="text-14px light dark text-left") {{ $t('Tags') }}
        v-list-item-action
          e-svg-icon plus-black
      v-list(class="tags-list")
        v-list-item-group(v-model="activeTagLink")
          template(v-for="(tag, index) in tags")
            v-tooltip(
              top
              :disabled="tag.name.length <= 10"
              :key="tag.id"
            )
              template(#activator="{ on, attrs }")
                v-hover(
                  v-slot="{ hover }"
                  :key="tag.id"
                )
                  v-list-item(
                    class="list__item"
                    @click="onTagClick(tag.id)"
                    v-on="on"
                    v-bind="attrs"
                  )
                    v-list-item-icon(
                      class="ma-0 d-flex justify-center align-center align-self-center h-100"
                    )
                      div(
                        class="tag-circle"
                        :style="{ backgroundColor: tag.color }"
                      )
                    v-list-item-title(class="link__text pl-3") {{ tag.name }}
                    v-list-item-action(
                      v-if="hover || onHoverTags[tag.id]"
                      class="ma-0 pr-3"
                    )
                      e-tags-menu-actions(
                        :value="onHoverTags[tag.id]"
                        :visibility-tags="visibilityTags"
                        :tag="tag"
                        @input="onHoverTags[tag.id] = !onHoverTags[tag.id]"
                        @onSettings="onTagSettingsClick"
                        @updateVisibility="updateTagVisibility"
                      )
              span {{ tag.name }}
      m-block-qr-code-promo
</template>

<script>
import { mapGetters } from 'vuex'
import { menuItems } from '~/const/menuItems'
import responsive from '~/mixins/pages/responsive'
import EButtonBurger from '~/components/elements/buttons/e-button-burger'
import ELink from '~/components/elements/links/e-link'
import EButtonDocumentUpload from '~/modules/documents/components/elements/e-button-document-upload'
import tags from '~/modules/tags/mixins/tags'
import ETagsMenuActions from '~/modules/tags/components/e-tags-menu-actions'
import EButtonTemplateUpload from '~/modules/templates/components/elements/e-button-template-upload'
import checkVisibility from '~/mixins/checkVisibility'
import MBlockQrCodePromo from '~/components/modules/blocks/concrete/m-block-qr-code-promo'
import EButtonContactCreate from '~/modules/contacts/components/e-button-contact-create'
import BillingUsageProgress from '~/modules/billing/components/billing-usage-progress'
import MBlockActivatePremium from '~/components/modules/blocks/concrete/m-block-activate-premium'

export default {
  components: {
    BillingUsageProgress,
    MBlockActivatePremium,
    EButtonContactCreate,
    MBlockQrCodePromo,
    ETagsMenuActions,
    EButtonDocumentUpload,
    EButtonTemplateUpload,
    EButtonBurger,
    ELink
  },
  mixins: [checkVisibility, tags, responsive],
  data: () => ({
    drawer: true,
    activeLink: null,
    activeTagLink: null,
    onHoverTags: {},
    visibilityTags: {},
    menu: false,
    tagsListMaxHeight: null
  }),
  computed: {
    ...mapGetters('tags', {
      tags: 'items'
    }),
    ...mapGetters('organizations', {
      organizations: 'items'
    }),
    items() {
      return menuItems
    }
  },
  watch: {
    tags(val) {
      if (val) {
        this.setActiveLink()
        for (const tag of this.tags) {
          this.$set(this.onHoverTags, tag.id, false)
          this.$set(this.visibilityTags, tag.id, tag.showInDocuments)
        }
      }
    },
    activeLink() {
      this.activeTagLink = null
    },
    activeTagLink() {
      this.activeLink = null
    }
  },
  created() {
    this.getTags()
  },
  methods: {
    isRouteActive(path) {
      return this.$route.path === path
    },
    setActiveLink() {
      const tagsPath = this._.map(this.tags, tag => ({ href: `/tags/${tag.id}` }))
      if (this.$route.name === 'tags-id') {
        this.activeLink = null
        this.activeTagLink = this._.findIndex(tagsPath, item => this.isRouteActive(item.href))
      } else {
        this.activeTagLink = null
        this.activeLink = this._.findIndex(this.items, item => this.isRouteActive(item.href))
      }
    },
    onTagClick(tagId) {
      this.$router.push({ name: 'tags-id', params: { id: tagId } })
    },
    onTagSettingsClick(tagId) {
      this.onHoverTags[tagId] = true
    },
    updateTagVisibility(tagId, val) {
      this.visibilityTags[tagId] = val
    }
  }
}
</script>

<style lang="scss" scoped>
.drawer {
  padding: 24px 8px;
  border: none !important;
}

.tags-list {
  overflow-y: auto;
}

.tag-circle {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.tags-btn {
  width: 100%;
  border-radius: 12px;
  min-height: 48px;
  background: none !important;

  &:before {
    border-radius: 12px;
  }

  @media (min-width: map-get($breakpoints, 'xl')) {
    margin-top: 16px;
  }
}

::v-deep(.v-navigation-drawer__content) {
  position: relative;
}

.link--spacer {
  margin-top: 16px;
  margin-bottom: 8px;
}
</style>
